import { useEffect, useMemo, useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import Pagination from "../../components/Pagination";
import SearchableSelect from "../../components/SearchableSelect";
import { useApi } from "../../hooks/useApi";
import {
  listEmployees,
  employeeStats,
  createEmployee,
  updateEmployee,
  markEmployeeLeft,
  rejoinEmployee,
  resetEmployeePassword,
  getEmployee,
  getPermissionsCatalog,
  syncEmployeePermissions,
} from "../../api/employeeService";
import { listDepartments } from "../../api/departmentService";
import { paginatedRows, paginatedMeta } from "../../utils/pagination";
import { formatDate, initials, serialNumber } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";
import { showCredentials } from "../../utils/credentials";
import { fetchAndDownload } from "../../utils/exportCsv";

// Mirror of EmployeePermission::CATALOG on the backend. Hardcoding here so
// the permission checkboxes always render even when the catalog endpoint
// hasn't been deployed yet or the request fails silently. The server-side
// validator is the real source of truth — if a new permission is added
// there, update this map too.
const PERMISSION_CATALOG = {
  "client.view": "View clients, businesses, and their fields",
  "client.add": "Create clients, businesses, field groups, and fields",
  "client.edit": "Edit existing clients, businesses, field groups, and fields",
  "client.task.oneoff": "Attach/detach one-off tasks to clients (with optional payment + receipt); auto-assigned to the employee",
  "payments.view": "View payments oversight — bills, KPIs, and receipts",
  "payments.edit": "Edit/cancel bills and record, edit, or delete receipts",
};

export default function EmployeesPage() {
  const [drawer, setDrawer] = useState(null); // null | { mode: 'create' } | { mode: 'edit', employee }
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, departmentId, statusFilter, perPage]);

  const employees = useApi(
    () => {
      const params = { page, per_page: perPage };
      if (debouncedSearch) params.search = debouncedSearch;
      if (departmentId) params.department_id = departmentId;
      if (statusFilter !== "all") params.status = statusFilter; // "active" | "inactive" | "left"
      return listEmployees(params);
    },
    [page, perPage, debouncedSearch, departmentId, statusFilter],
  );
  const depts = useApi(() => listDepartments({ per_page: 100 }));
  const stats = useApi(() => employeeStats());
  const rows = paginatedRows(employees.data);
  const meta = paginatedMeta(employees.data);
  const deptRows = paginatedRows(depts.data);
  const deptOptions = useMemo(
    () => deptRows.map((d) => ({ id: d.id, label: d.name })),
    [deptRows],
  );
  const counts = stats.data?.data || { total: 0, active: 0, inactive: 0, left: 0 };

  const exportEmployees = async () => {
    setExporting(true);
    try {
      await fetchAndDownload("/employees/export", {}, "employees");
      showToast("success", "Export downloaded");
    } catch (err) {
      showApiError(err);
    } finally {
      setExporting(false);
    }
  };

  const onMarkLeft = async (e) => {
    if (!window.confirm(`Mark ${e.name} as left? Their login will be disabled and any active sessions will be revoked.`)) return;
    try {
      await markEmployeeLeft(e.id);
      showToast("success", `${e.name} marked as left`);
      employees.reload();
      stats.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  const onRejoin = async (e) => {
    if (!window.confirm(`Rejoin ${e.name}? Their login will be re-enabled.`)) return;
    try {
      await rejoinEmployee(e.id);
      showToast("success", `${e.name} rejoined`);
      employees.reload();
      stats.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  const onResetPassword = async (e) => {
    if (!window.confirm(
      `Reset ${e.name}'s password? They'll be logged out everywhere and must use the new temp password.`,
    )) return;
    try {
      const res = await resetEmployeePassword(e.id);
      showCredentials(res);
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Employees</h1>
          <div className="page-sub">{meta?.total ?? rows.length} team members</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={exportEmployees} disabled={exporting}>
            <Icon name="download" size={14} /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
          <button className="btn btn-primary" onClick={() => setDrawer({ mode: "create" })}>
            <Icon name="plus" size={14} /> Add employee
          </button>
        </div>
      </div>

      <div className="filter-bar">
        {[
          { id: "all", label: "Current", count: counts.total },
          { id: "active", label: "Active", count: counts.active },
          { id: "inactive", label: "Inactive", count: counts.inactive },
          { id: "left", label: "Left", count: counts.left },
        ].map((f) => (
          <button
            key={f.id}
            className={`chip ${statusFilter === f.id ? "active" : ""}`}
            onClick={() => setStatusFilter(f.id)}
          >
            {f.label} <span className="chip-count">{f.count}</span>
          </button>
        ))}
        <SearchableSelect
          value={departmentId}
          onChange={(v) => setDepartmentId(v ?? "")}
          options={deptOptions}
          placeholder="All departments"
          searchPlaceholder="Search departments…"
          width={200}
        />
        <div style={{ position: "relative", flex: 1, maxWidth: 360, marginLeft: "auto" }}>
          <Icon
            name="search"
            size={13}
            style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-400)", pointerEvents: "none" }}
          />
          <input
            className="input"
            style={{ paddingLeft: 30 }}
            placeholder="Search by name, employee ID or phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-body no-pad">
          {employees.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={5} /></div>
          ) : rows.length === 0 ? (
            <EmptyState icon="user" title="No employees match" hint={debouncedSearch ? "Try a different search." : "Onboard your first team member."} />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 50 }} className="t-num">#</th>
                  <th>Name</th>
                  <th>Employee ID</th>
                  <th>Department</th>
                  <th>Phone</th>
                  <th style={{ textAlign: "center" }} title="client.view / client.add / client.edit / client.task.oneoff — client access">Clients</th>
                  <th style={{ textAlign: "center" }} title="payments.view / payments.edit — payments oversight access">Payments</th>
                  <th style={{ textAlign: "center" }} title="org-kb.view / org-kb.edit / org-kb.delete — org drive & org fields access">Org KB</th>
                  <th>Status</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e, i) => (
                  <tr key={e.id}>
                    <td className="t-num t-muted">{serialNumber(meta, i)}</td>
                    <td>
                      <div className="row gap-sm">
                        <div className="avatar avatar-sm" style={{ background: `hsl(${(e.id * 47) % 360} 60% 55%)` }}>
                          {initials(e.name)}
                        </div>
                        <div>
                          <div className="t-primary">{e.name}</div>
                          <div className="t-muted text-xs">{e.designation || e.user?.phone}</div>
                        </div>
                      </div>
                    </td>
                    <td className="t-mono">{e.employee_id}</td>
                    <td><span className="badge badge-neutral">{e.department?.name}</span></td>
                    <td className="t-muted">{e.user?.phone}</td>
                    <ClientCell employee={e} />
                    <PaymentsCell employee={e} />
                    <OrgKbCell employee={e} />

                    <td>
                      {e.left_at ? (
                        <span className="badge badge-warning" title={`Left on ${formatDate(e.left_at)}`}>
                          Left · {formatDate(e.left_at)}
                        </span>
                      ) : e.user?.status ? (
                        <span className="badge badge-success">Active</span>
                      ) : (
                        <span className="badge badge-neutral">Inactive</span>
                      )}
                    </td>
                    <td>
                      <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
                        {!e.left_at && (
                          <button
                            className="icon-btn"
                            title="Edit"
                            onClick={() => setDrawer({ mode: "edit", employee: e })}
                          >
                            <Icon name="edit" size={13} />
                          </button>
                        )}
                        {!e.left_at && (
                          <button
                            className="icon-btn"
                            title="Reset password (issues a one-time temp password)"
                            onClick={() => onResetPassword(e)}
                          >
                            <Icon name="key" size={13} />
                          </button>
                        )}
                        {e.left_at ? (
                          <button
                            className="icon-btn"
                            title="Rejoin"
                            onClick={() => onRejoin(e)}
                          >
                            <Icon name="user" size={13} />
                          </button>
                        ) : (
                          <button
                            className="icon-btn"
                            title="Mark as left"
                            onClick={() => onMarkLeft(e)}
                            style={{ color: "var(--danger)" }}
                          >
                            <Icon name="log-out" size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pagination
            meta={meta}
            page={page}
            onPageChange={setPage}
            perPage={perPage}
            onPerPageChange={setPerPage}
            disabled={employees.loading}
          />
        </div>
      </div>

      <EmployeeDrawer
        open={!!drawer}
        mode={drawer?.mode}
        employee={drawer?.employee}
        depts={deptRows}
        onClose={() => setDrawer(null)}
        onSaved={() => {
          setDrawer(null);
          employees.reload();
          stats.reload();
        }}
      />
    </>
  );
}

function EmployeeDrawer({ open, mode, employee, depts, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", designation: "", phone: "", username: "", employee_id: "", department_id: "", status: true });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  // Permission editor: hardcoded catalog ensures the section always renders;
  // we still try to refresh from the server in case it grew. The employee's
  // current set is fetched fresh on edit so checkboxes reflect the saved
  // state, not the possibly-stale list row.
  const [permCatalog, setPermCatalog] = useState(PERMISSION_CATALOG);
  const [perms, setPerms] = useState([]);
  const [permsBaseline, setPermsBaseline] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && employee) {
      setForm({
        name: employee.name || "",
        designation: employee.designation || "",
        phone: employee.user?.phone || "",
        username: employee.user?.username || "",
        employee_id: employee.employee_id || "",
        department_id: employee.department_id ? String(employee.department_id) : "",
        status: employee.user?.status === undefined ? true : !!employee.user?.status,
      });
    } else {
      setForm({ name: "", designation: "", phone: "", username: "", employee_id: "", department_id: "", status: true });
    }
    setErrors({});
  }, [open, mode, employee]);

  // Load the permission catalog once per drawer open; for edits, also fetch
  // the employee's current perms (the list endpoint doesn't include them, to
  // keep that response small).
  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      try {
        // The axios interceptor unwraps the body, so `cat` is already the
        // `{ status, data }` shape — the catalog sits at `cat.data`, not
        // `cat.data.data`. Only adopt the server response if it looks like
        // a non-empty catalog; otherwise keep the hardcoded fallback so the
        // checkbox UI never blanks out.
        const cat = await getPermissionsCatalog();
        if (!alive) return;
        const fromServer = cat?.data;
        if (fromServer && typeof fromServer === "object" && Object.keys(fromServer).length > 0) {
          setPermCatalog(fromServer);
        }
      } catch {
        /* non-fatal — fall back to the hardcoded catalog already in state */
      }
      if (mode === "edit" && employee?.id) {
        try {
          const full = await getEmployee(employee.id);
          if (!alive) return;
          const list = Array.isArray(full?.data?.permissions) ? full.data.permissions : [];
          setPerms(list);
          setPermsBaseline(list);
        } catch {
          /* leave perms empty; will save nothing if no changes */
        }
      } else {
        setPerms([]);
        setPermsBaseline([]);
      }
    })();
    return () => { alive = false; };
  }, [open, mode, employee?.id]);

  const togglePerm = (name) => {
    setPerms((p) => p.includes(name) ? p.filter((x) => x !== name) : [...p, name]);
  };

  const permsChanged = mode === "edit" && (
    perms.length !== permsBaseline.length
    || perms.some((p) => !permsBaseline.includes(p))
  );

  const submit = async () => {
    setErrors({});
    setSaving(true);
    try {
      if (mode === "edit") {
        const payload = {
          name: form.name,
          designation: form.designation?.trim() ? form.designation.trim() : null,
          employee_id: form.employee_id,
          department_id: form.department_id ? Number(form.department_id) : null,
          phone: form.phone.trim(),
          status: form.status,
        };
        if (form.username?.trim()) payload.username = form.username.trim();
        await updateEmployee(employee.id, payload);
        if (permsChanged) {
          await syncEmployeePermissions(employee.id, perms);
        }
        showToast("success", "Employee updated");
      } else {
        // createEmployee returns the created row (with id) so we can sync
        // any pre-checked permissions in the same flow as edit. Backend
        // also expects sync via the dedicated endpoint, not the create
        // body, so we issue a second call when perms were ticked.
        const res = await createEmployee({
          ...form,
          designation: form.designation?.trim() ? form.designation.trim() : null,
          username: form.username?.trim() ? form.username.trim() : undefined,
        });
        const newId = res?.data?.id;
        if (newId && perms.length > 0) {
          await syncEmployeePermissions(newId, perms);
        }
        if (!showCredentials(res)) showToast("success", "Employee created");
      }
      onSaved?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={mode === "edit" ? `Edit ${employee?.name || "employee"}` : "New employee"}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Create"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <Field label="Name" error={errors.name?.[0]}>
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field
          label="Designation"
          error={errors.designation?.[0]}
          hint="Job title — separate from Department. Leave blank if not applicable."
        >
          <input
            className="input"
            value={form.designation}
            onChange={(e) => setForm({ ...form, designation: e.target.value })}
            placeholder="e.g. Senior Accountant"
          />
        </Field>
        <Field label="Phone (with country code)" error={errors.phone?.[0]}>
          <input
            className="input"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="+919876543210"
          />
        </Field>
        <Field label="Username" error={errors.username?.[0]}>
          <input
            className="input"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            placeholder={mode === "edit" ? "" : "Leave blank to auto-generate from phone"}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
          <div className="field-hint">Login username. Lowercase letters, digits, and . _ - only.</div>
        </Field>
        <Field label="Employee ID" error={errors.employee_id?.[0]}>
          <input className="input" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} placeholder="EMP-0001" />
        </Field>
        <Field label="Department" error={errors.department_id?.[0]}>
          <SearchableSelect
            value={form.department_id}
            onChange={(v) => setForm({ ...form, department_id: v ?? "" })}
            options={depts.map((d) => ({ id: d.id, label: d.name }))}
            placeholder="— Select —"
            searchPlaceholder="Search departments…"
            width="100%"
          />
        </Field>
        {mode === "edit" && (
          <div className="row gap-sm">
            <input
              id="emp-status"
              type="checkbox"
              checked={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.checked })}
            />
            <label htmlFor="emp-status" style={{ flex: 1, cursor: "pointer", marginBottom: 0 }}>
              <div className="t-primary text-sm fw-500">Active</div>
              <div className="t-muted text-xs" style={{ marginTop: 2 }}>
                Inactive employees can't sign in and any active sessions are revoked.
              </div>
            </label>
          </div>
        )}

        <div className="field">
          <label className="field-label">Permissions</label>
            <div className="field-hint" style={{ marginBottom: 6 }}>
              Grants extend what this employee can see and do beyond their own
              tasks. {mode === "edit" ? "Saved when you hit Save changes." : "Saved with the new employee."}
            </div>
            <div className="col gap-sm">
              {Object.entries(permCatalog).map(([name, desc]) => {
                const checked = perms.includes(name);
                const id = `perm-${name}`;
                return (
                  <label key={name} htmlFor={id} className="row gap-sm" style={{ cursor: "pointer", alignItems: "flex-start" }}>
                    <input
                      id={id}
                      type="checkbox"
                      checked={checked}
                      onChange={() => togglePerm(name)}
                      style={{ marginTop: 3 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="t-primary text-sm fw-500">
                        <span className="t-mono" style={{ fontSize: 12 }}>{name}</span>
                      </div>
                      <div className="t-muted text-xs" style={{ marginTop: 2 }}>
                        {desc}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
      </div>
    </Drawer>
  );
}

// Compact single-column view of the three client-access grants (view/add/edit).
// Renders a small green pill per granted level so the table stays narrow
// instead of three separate Yes/No columns. Mirrors OrgKbCell.
function ClientCell({ employee }) {
  const perms = Array.isArray(employee?.permissions) ? employee.permissions : [];
  const has = (name) => perms.some((p) => p?.name === name);
  const levels = [
    { key: "client.view", label: "View" },
    { key: "client.add", label: "Add" },
    { key: "client.edit", label: "Edit" },
    { key: "client.task.oneoff", label: "Tasks" },
  ];
  const granted = levels.filter((l) => has(l.key));

  return (
    <td style={{ textAlign: "center" }}>
      {granted.length === 0 ? (
        <span className="t-muted" style={{ fontSize: 12 }} title="No client access">
          No access
        </span>
      ) : (
        <div className="row gap-sm" style={{ justifyContent: "center", flexWrap: "wrap" }}>
          {granted.map((l) => (
            <span
              key={l.key}
              className="badge badge-success"
              title={`${l.key} granted`}
              style={{ fontSize: 10 }}
            >
              {l.label}
            </span>
          ))}
        </div>
      )}
    </td>
  );
}

// Compact single-column view of the payments-oversight grants (view/edit).
function PaymentsCell({ employee }) {
  const perms = Array.isArray(employee?.permissions) ? employee.permissions : [];
  const has = (name) => perms.some((p) => p?.name === name);
  const levels = [
    { key: "payments.view", label: "View" },
    { key: "payments.edit", label: "Edit" },
  ];
  const granted = levels.filter((l) => has(l.key));

  return (
    <td style={{ textAlign: "center" }}>
      {granted.length === 0 ? (
        <span className="t-muted" style={{ fontSize: 12 }} title="No payments access">
          No access
        </span>
      ) : (
        <div className="row gap-sm" style={{ justifyContent: "center", flexWrap: "wrap" }}>
          {granted.map((l) => (
            <span
              key={l.key}
              className="badge badge-success"
              title={`${l.key} granted`}
              style={{ fontSize: 10 }}
            >
              {l.label}
            </span>
          ))}
        </div>
      )}
    </td>
  );
}

// Compact single-column view of the three org knowledge-base grants
// (drive + fields share the same org-kb.* keys). Renders a small green
// pill per granted level so the table stays narrow instead of adding
// three separate Yes/No columns.
function OrgKbCell({ employee }) {
  const perms = Array.isArray(employee?.permissions) ? employee.permissions : [];
  const has = (name) => perms.some((p) => p?.name === name);
  const levels = [
    { key: "org-kb.view", label: "View" },
    { key: "org-kb.edit", label: "Edit" },
    { key: "org-kb.delete", label: "Delete" },
  ];
  const granted = levels.filter((l) => has(l.key));

  return (
    <td style={{ textAlign: "center" }}>
      {granted.length === 0 ? (
        <span className="t-muted" style={{ fontSize: 12 }} title="No org knowledge base access">
          No access
        </span>
      ) : (
        <div className="row gap-sm" style={{ justifyContent: "center", flexWrap: "wrap" }}>
          {granted.map((l) => (
            <span
              key={l.key}
              className="badge badge-success"
              title={`${l.key} granted`}
              style={{ fontSize: 10 }}
            >
              {l.label}
            </span>
          ))}
        </div>
      )}
    </td>
  );
}

function Field({ label, error, children }) {
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      {children}
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}
